import { useState } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    Card, 
    CardContent, 
    IconButton, 
    Tooltip, 
    Chip,
    CircularProgress,
    Stack,
    Alert
} from '@mui/material';
import { 
    Mail, 
    Trash2, 
    CheckCircle, 
    AlertCircle, 
    Star, 
    RefreshCw,
    RefreshCcw,
    Plus
} from 'lucide-react';
import { 
    useGetEmailAccounts, 
    useDeleteEmailAccount, 
    useSetDefaultEmailAccount, 
    useTestEmailAccount,
    useSyncEmailAccount,
    useGetGmailOAuthUrl 
} from '../react-query/email-account-queries';
import { EmailAccount } from '../types/user';

export default function EmailAccounts() {
    const { data: accountsData, isLoading: isLoadingAccounts, error: accountsError } = useGetEmailAccounts();
    const deleteAccountMutation = useDeleteEmailAccount();
    const setDefaultAccountMutation = useSetDefaultEmailAccount();
    const testAccountMutation = useTestEmailAccount();
    const syncAccountMutation = useSyncEmailAccount();
    
    // We use refetch to trigger the query manually
    const { refetch: getOAuthUrl, isFetching: isGettingUrl, error: oauthError } = useGetGmailOAuthUrl();

    const [testingId, setTestingId] = useState<string | null>(null);
    const [syncingId, setSyncingId] = useState<string | null>(null);

    const handleConnectGmail = async () => {
        try {
            const result = await getOAuthUrl();
            if (result.data?.success && result.data.data?.authorizationUrl) {
                window.location.href = result.data.data.authorizationUrl;
            } else {
                console.error("Failed to get authorization URL", result);
            }
        } catch (error) {
            console.error("Error getting authorization URL", error);
        }
    };

    const handleDelete = (id: string) => {
        if (window.confirm('Are you sure you want to remove this email account?')) {
            deleteAccountMutation.mutate(id);
        }
    };

    const handleSetDefault = (id: string) => {
        setDefaultAccountMutation.mutate(id);
    };

    const handleTestConnection = (id: string) => {
        setTestingId(id);
        testAccountMutation.mutate(id, {
            onSettled: () => setTestingId(null),
            onSuccess: () => alert('Connection test successful!'), // Replace with toast if available
            onError: () => alert('Connection test failed!') // Replace with toast if available
        });
    };

    const handleSync = (id: string) => {
        setSyncingId(id);
        syncAccountMutation.mutate(id, {
            onSettled: () => setSyncingId(null),
            onSuccess: () => alert('Synchronization started!'), // Replace with toast if available
            onError: () => alert('Synchronization failed!') // Replace with toast if available
        });
    };

    if (isLoadingAccounts) {
        return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    }

    if (accountsError) {
        return <Alert severity="error">Failed to load email accounts</Alert>;
    }

    const accounts = accountsData?.data || [];

    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" component="h2" fontWeight="bold">
                        Connected Email Accounts
                    </Typography>
                    <Button 
                        variant="contained" 
                        startIcon={isGettingUrl ? <CircularProgress size={20} color="inherit" /> : <Plus size={18} />}
                        onClick={handleConnectGmail}
                        disabled={isGettingUrl}
                    >
                        {isGettingUrl ? 'Connecting...' : 'Connect Gmail'}
                    </Button>
                </Stack>
                {oauthError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Failed to initiate Gmail connection. Please try again.
                    </Alert>
                )}

                {accounts.length === 0 ? (
                    <Box 
                        display="flex" 
                        flexDirection="column" 
                        alignItems="center" 
                        justifyContent="center" 
                        py={6}
                        bgcolor="grey.50"
                        borderRadius={1}
                        border="1px dashed"
                        borderColor="grey.300"
                    >
                        <Mail className="w-12 h-12 text-gray-400 mb-2" />
                        <Typography color="textSecondary">
                            No email accounts connected yet.
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Connect your Gmail account to start sending outreach emails.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        {accounts.map((account: EmailAccount) => (
                            <Box 
                                key={account.id} 
                                sx={{ 
                                    p: 2, 
                                    border: '1px solid', 
                                    borderColor: 'divider', 
                                    borderRadius: 1,
                                    bgcolor: 'background.paper'
                                }}
                            >
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box 
                                            sx={{ 
                                                width: 40, 
                                                height: 40, 
                                                borderRadius: '50%', 
                                                bgcolor: 'primary.light',
                                                color: 'primary.main',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Mail size={20} />
                                        </Box>
                                        <Box>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography variant="subtitle1" fontWeight="medium">
                                                    {account.emailAddress}
                                                </Typography>
                                                {account.isDefault && (
                                                    <Chip label="Default" size="small" color="primary" variant="outlined" />
                                                )}
                                            </Stack>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                                                    {account.provider.toLowerCase()}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">•</Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    color={account.status === 'CONNECTED' ? 'success.main' : 'error.main'} 
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                                >
                                                    {account.status === 'CONNECTED' ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                                                    {account.status}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    </Stack>

                                    <Stack direction="row" spacing={1}>
                                        {!account.isDefault && (
                                            <Tooltip title="Set as default">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleSetDefault(account.id)}
                                                    disabled={setDefaultAccountMutation.isPending}
                                                >
                                                    <Star size={18} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Sync now">
                                            <IconButton 
                                                size="small" 
                                                onClick={() => handleSync(account.id)}
                                                disabled={syncingId === account.id || syncAccountMutation.isPending}
                                            >
                                                {syncingId === account.id ? <CircularProgress size={18} /> : <RefreshCcw size={18} />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Test connection">
                                            <IconButton 
                                                size="small" 
                                                onClick={() => handleTestConnection(account.id)}
                                                disabled={testingId === account.id || testAccountMutation.isPending}
                                            >
                                                {testingId === account.id ? <CircularProgress size={18} /> : <RefreshCw size={18} />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Remove account">
                                            <IconButton 
                                                size="small" 
                                                color="error"
                                                onClick={() => handleDelete(account.id)}
                                                disabled={deleteAccountMutation.isPending}
                                            >
                                                <Trash2 size={18} />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
}
